import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { APP_VERSION_POLICY_FIELD_COPY, DnmRequestFailure } from '../../api/dnmFailure';
import { updateAppVersionPolicy } from '../../api/endpoints/ops';
import type { AppVersionPolicyEntry, AppVersionPolicyField, AppVersionPolicyUpdate } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { DialogShell } from '../../components/ui/DialogShell';
import { toast } from '../../components/ui/Toast';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import formStyles from '../../components/domain/FormModal.module.css';
import { APP_VERSION_POLICY_KEY, useAppLabels } from './appUpdateLabels';
import { compareAppVersions, isAppVersion, raisesMinimum } from './appVersion';
import styles from './AppUpdatesTab.module.css';

export const NOTE_MAX = 140;
const URL_MAX = 500;

/** Client-side copy for the checks that mirror C4; the server stays the authority. */
const COPY = {
  version: 'Enter a version like 1.4.0 or 1.4.0+57.',
  order: 'The minimum can’t be higher than the latest version.',
  storeUrl: APP_VERSION_POLICY_FIELD_COPY.storeUrl,
  note: APP_VERSION_POLICY_FIELD_COPY['releaseNotes.en'],
};

type Values = Record<AppVersionPolicyField, string>;
type Errors = Partial<Record<AppVersionPolicyField, string>>;

const valuesOf = (e: AppVersionPolicyEntry): Values => ({
  minSupportedVersion: e.minSupportedVersion ?? '',
  latestVersion: e.latestVersion ?? '',
  storeUrl: e.storeUrl ?? '',
  'releaseNotes.en': e.releaseNotes.en ?? '',
  'releaseNotes.hi': e.releaseNotes.hi ?? '',
});

const orNull = (v: string) => v.trim() || null;

function bodyOf(v: Values): AppVersionPolicyUpdate {
  return {
    minSupportedVersion: orNull(v.minSupportedVersion),
    latestVersion: orNull(v.latestVersion),
    storeUrl: orNull(v.storeUrl),
    releaseNotes: { en: orNull(v['releaseNotes.en']), hi: orNull(v['releaseNotes.hi']) },
  };
}

/** Note length in code points, as rhino-api counts it (`codePointCount`), not UTF-16 units. */
const noteLength = (s: string) => [...s].length;

function validate(v: Values): Errors {
  const b = bodyOf(v);
  const errors: Errors = {};
  if (b.minSupportedVersion && !isAppVersion(b.minSupportedVersion)) errors.minSupportedVersion = COPY.version;
  if (b.latestVersion && !isAppVersion(b.latestVersion)) errors.latestVersion = COPY.version;
  if (
    !errors.minSupportedVersion &&
    !errors.latestVersion &&
    b.minSupportedVersion &&
    b.latestVersion &&
    compareAppVersions(b.minSupportedVersion, b.latestVersion) > 0
  ) {
    errors.minSupportedVersion = COPY.order;
  }
  if (b.storeUrl && (!b.storeUrl.startsWith('https://') || b.storeUrl.length > URL_MAX)) errors.storeUrl = COPY.storeUrl;
  if (noteLength(b.releaseNotes.en ?? '') > NOTE_MAX) errors['releaseNotes.en'] = COPY.note;
  if (noteLength(b.releaseNotes.hi ?? '') > NOTE_MAX) errors['releaseNotes.hi'] = COPY.note;
  return errors;
}

/** Edit one app × platform row (manager only). Raising the minimum confirms in place before saving. */
export function AppUpdateDialog({ entry, onClose }: { entry: AppVersionPolicyEntry; onClose: () => void }) {
  const { t } = useTranslation();
  const labels = useAppLabels();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Values>(() => valuesOf(entry));
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: AppVersionPolicyUpdate) => updateAppVersionPolicy(entry.app, entry.platform, body),
    onSuccess: () => {
      toast.success('App update settings saved.');
      void queryClient.invalidateQueries({ queryKey: APP_VERSION_POLICY_KEY });
      onClose();
    },
    onError: (err) => {
      // The input is always kept; the plain sentence lands under the field the server named.
      const failure = err instanceof DnmRequestFailure ? err : null;
      const field = failure?.code === 'APP_VERSION_POLICY_INVALID' ? (failure.field as AppVersionPolicyField | undefined) : undefined;
      if (field) setErrors({ [field]: failure!.message });
      else setFormError(err instanceof Error ? err.message : 'Check the entered information and try again.');
    },
  });

  const dirty = useIsDirty({ ...values });
  const dismiss = useDialogDismiss({ dirty, busy: save.isPending, onClose });

  const newMinimum = orNull(values.minSupportedVersion);
  const raising = raisesMinimum(entry.minSupportedVersion, newMinimum);

  const set = (key: AppVersionPolicyField) => (value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (save.isPending) return;
    const found = validate(values);
    setErrors(found);
    setFormError(null);
    if (Object.values(found).some(Boolean)) return;
    save.mutate(bodyOf(values));
  };

  const title = t('settings.appUpdates.dialogTitle', { app: labels.app(entry.app), platform: labels.platform(entry.platform) });

  const field = (
    id: AppVersionPolicyField,
    label: string,
    control: (p: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode,
    extra?: ReactNode,
  ) => {
    const inputId = `app-update-${id.replace('.', '-')}`;
    const error = errors[id];
    return (
      <div className={styles.field}>
        <label className={formStyles.label} htmlFor={inputId}>
          {label}
        </label>
        {control({ id: inputId, describedBy: error ? `${inputId}-error` : undefined, invalid: !!error })}
        {extra}
        {error && (
          <p id={`${inputId}-error`} className={styles.fieldError}>
            {error}
          </p>
        )}
      </div>
    );
  };

  const textInput = (id: AppVersionPolicyField, label: string, placeholder: string, hint?: string) =>
    field(
      id,
      label,
      ({ id: inputId, describedBy, invalid }) => (
        <input
          id={inputId}
          className={formStyles.input}
          value={values[id]}
          placeholder={placeholder}
          spellCheck={false}
          autoComplete="off"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(e) => set(id)(e.target.value)}
        />
      ),
      hint ? <p className={styles.fieldHint}>{hint}</p> : undefined,
    );

  const noteInput = (id: 'releaseNotes.en' | 'releaseNotes.hi', label: string) =>
    field(
      id,
      label,
      ({ id: inputId, describedBy, invalid }) => (
        <textarea
          id={inputId}
          className={`${formStyles.input} ${styles.textarea}`}
          value={values[id]}
          rows={2}
          lang={id === 'releaseNotes.hi' ? 'hi' : undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(e) => set(id)(e.target.value)}
        />
      ),
      <p className={noteLength(values[id].trim()) > NOTE_MAX ? `${styles.counter} ${styles.counterOver}` : styles.counter}>
        {t('settings.appUpdates.counter', { count: noteLength(values[id].trim()), max: NOTE_MAX })}
      </p>,
    );

  return (
    <DialogShell dismiss={dismiss} label={title} scrimClassName={formStyles.scrim} panelClassName={formStyles.panel}>
      <h2 className={styles.dialogTitle}>{title}</h2>
      <form onSubmit={submit} noValidate>
        <div className={styles.pair}>
          {textInput('minSupportedVersion', t('settings.appUpdates.minimumLabel'), t('settings.appUpdates.versionPlaceholder'), t('settings.appUpdates.minimumHint'))}
          {textInput('latestVersion', t('settings.appUpdates.latestLabel'), t('settings.appUpdates.versionPlaceholder'), t('settings.appUpdates.latestHint'))}
        </div>
        {textInput('storeUrl', t('settings.appUpdates.storeLinkLabel'), t('settings.appUpdates.storeLinkPlaceholder'))}
        {noteInput('releaseNotes.en', t('settings.appUpdates.notesEnLabel'))}
        {noteInput('releaseNotes.hi', t('settings.appUpdates.notesHiLabel'))}

        {raising && newMinimum && (
          <p className={styles.raise} role="status">
            {t('settings.appUpdates.raiseWarning', { version: newMinimum })}
          </p>
        )}
        {formError && (
          <div className={formStyles.error} role="alert">
            {formError}
          </div>
        )}

        <div className={styles.dialogActions}>
          <Button variant="ghost" onClick={dismiss.requestClose} disabled={save.isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant={raising ? 'danger' : 'primary'} busy={save.isPending}>
            {save.isPending ? t('settings.appUpdates.saving') : raising ? t('settings.appUpdates.saveAndBlock') : t('settings.appUpdates.save')}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
